import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { Observable } from 'rxjs';
import { map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { Router } from '@angular/router';
import { AnalyticsService } from '@core/services/analytics.service';
import { Action, select, Store } from '@ngrx/store';
import { HotelsService } from '@services/hotels.service';
import { LandingService } from '@services/landing.service';
import { selectIsLanding } from '@store/context/context.selector';
import { selectRouterQueryParams } from '@store/router/router.selector';
import { TurnLandingViewOff } from '@store/context/context.actions';
import { Type } from './results.actions';

import * as actions from './results.actions';
import { IResults } from './results.state';

@Injectable()
export class ResultsEffecs {
  constructor(
    private actions$: Actions,
    private hotelService: HotelsService,
    private landingSvc: LandingService,
    private analyticsService: AnalyticsService,
    private store$: Store<IResults>,
    private router: Router,
  ) {}

  public NavigationSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      mergeMap((action: any) => {
        window.scrollTo(0, 0);
        const entity = action.payload.routerState.entity;
        const queryParams = action.payload.routerState.queryParams;
        const url = action.payload.routerState.url;
        const params = {
          ...entity,
          ...queryParams,
          url,
        };
        this.hotelService.setTrivagoInfo(queryParams);
        const dispatchActions: Action[] = [actions.hotelRequestGet({ payload: params })];
        if (queryParams.date) {
          dispatchActions.push(new TurnLandingViewOff());
        }
        return dispatchActions;
      }),
    ),
  );

  public LandingFilterRequestGet: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(Type.LANDING_FILTER_REQUEST_GET),
      switchMap((action: any) => {
        const filterQuery = this.landingSvc.getFilterCategory(action.payload);
        return this.hotelService.getHotels({ ...action.payload.query, ...filterQuery }).pipe(
          map((landingResults: any): any => {
            if (landingResults.error) {
              return actions.hotelRequestError({
                payload: { error: landingResults.error },
              });
            }
            return actions.hotelsRequestSuccess({
              payload: {
                customBanner: landingResults.customBanner,
                filters: {
                  applied: landingResults.appliedFilters,
                  list: landingResults.filters,
                },
                hotels: landingResults.hotels,
                pagination: landingResults.pagination,
              },
            });
          }),
        );
      }),
    ),
  );

  public HotelsRequestGet$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(Type.HOTELS_REQUEST_GET),
      withLatestFrom(this.store$.pipe(select(selectIsLanding))),
      switchMap(([action, isLanding]: any) => {
        const query = {
          ...(action as any).payload,
          isLanding,
        };
        return this.hotelService.getHotels(query).pipe(
          map((response: any): any => {
            if (response.error) {
              return actions.hotelRequestError({
                payload: {
                  error: response.error,
                },
              });
            }
            response.appliedFilters.map((applied) => {
              if (applied.type === 'DISCOUNT_RANGE') {
                applied.type = 'DISCOUNTS';
              }
              applied.values = applied.values?.map((value) => {
                return {
                  ...value,
                  isApplied: true,
                };
              });
              if (applied.values?.length === 0) {
                query[applied.type].split(',').forEach((appliedQuery) => {
                  applied.values.push({ code: appliedQuery, description: null });
                });
              }
              if (applied.type === 'DISCOUNTS' && applied.values?.length > 1) {
                applied.values.pop();
              }
            });

            response.filters.forEach((filters) => {
              filters.values = filters.values?.map((value) => {
                return { ...value, isApplied: false };
              });
            });

            const results = {
              customBanner: response.customBanner,
              destination: response.destination,
              filters: {
                applied: response.appliedFilters,
                list: response.filters,
              },
              hotels: response.hotels,
              pagination: response.pagination,
            };

            if (results.hotels && results.hotels.length) {
              const trackingData = {
                data: results,
                query,
              };
              this.analyticsService.trackProductImpressions(trackingData);

              if (
                results.pagination.offset === 0 &&
                !query.sortCriteria &&
                (!results.filters.applied || !results.filters.applied.length)
              ) {
                this.analyticsService.trackDataHTL(trackingData);
              }
            }
            if (isLanding) {
              this.hotelService.submitP13nEventLanding(query.id).subscribe(() => {});
            }
            if (
              isLanding &&
              (query.url.includes('/alojamientos/') ||
                query.url.includes('/servicios/') ||
                query.url.includes('-estrellas/'))
            ) {
              return actions.landigFilterRequestGet({
                payload: { query, filters: results.filters },
              });
            } else {
              return actions.hotelsRequestSuccess({ payload: results });
            }
          }),
        );
      }),
    ),
  );

  public RecommendedHotelsRequestGet$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.recommendedHostelRequestGet),
      switchMap((action: any) => {
        const query = {
          ...(action as any).payload,
        };
        return this.hotelService.getRecommendedHotels(query).pipe(
          map((response: any): any => {
            return actions.recommendedHotelRequestSuccess({ payload: response });
          }),
        );
      }),
    ),
  );

  public Filtering$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.toggleFilter),
        withLatestFrom(this.store$.pipe(select(selectRouterQueryParams))),
        tap(([action, queryParams]) => {
          let newQueryParams = { ...queryParams };
          for (let type of Object.keys(action.payload)) {
            if (type === 'priceBy') {
              delete newQueryParams.PRICE_RANGE;
            }

            if (type === 'FLEXIBLE') {
              type = type.toLowerCase();
            }
            newQueryParams = {
              ...newQueryParams,
              ...{ [type]: action.payload[type] },
            };
            if (!!!newQueryParams[type]) {
              delete newQueryParams[type];
            }
          }
          this.router.navigate([], {
            queryParams: newQueryParams,
          });
        }),
      ),
    { dispatch: false },
  );

  public AllLocationRequestGet$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.allLocationRequestGet),
      switchMap((action: any) => {
        const params = {
          ...action.payload.params,
          id: action.payload.entity.id,
          name: action.payload.entity.name,
        };
        return this.hotelService.getAll(params).pipe(
          map((response: any): any => {
            return actions.allLocationRequestSuccess({ payload: response });
          }),
        );
      }),
    ),
  );
}
